import React from 'react';
import { TS1_PORTAL } from '../config/ts1Portal';

const guides = [
  { id: 'getting-started', title: 'Getting Started' },
  { id: 'view-sensor-data', title: 'How to View Sensor Data' },
  { id: 'add-sensor', title: 'How to Add a Sensor' },
  { id: 'configure-base-unit-ts1-portal', title: 'Configure a Base Unit with TS1 Portal (mobile)' },
  { id: 'ems-arb-network', title: 'EMS Base Units & ARB Alarm Modules — Network Setup' },
  { id: 'manage-thresholds', title: 'How to View or Edit Thresholds' },
  { id: 'acknowledge-alarm', title: 'How to Acknowledge an Alarm' },
  { id: 'manage-users', title: 'How to Add or Remove a User' },
  { id: 'manage-zones', title: 'How to View, Add, or Remove Zones' },
  { id: 'edit-alarm-notes', title: 'How to Edit Notes on an Alarm' },
  { id: 'troubleshooting', title: 'Troubleshooting & FAQ' },
];

export default function HelpPage() {
  return (
    <div className="p-8 max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">Knowledge Base & Guides</h1>
      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Welcome to the TS1 Dashboard Knowledge Base. Here you'll find step-by-step guides for common tasks and troubleshooting tips.
      </p>
      <nav className="mb-8">
        <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Table of Contents</h2>
        <ul className="list-disc pl-6 space-y-1 text-blue-700 dark:text-blue-400">
          {guides.map(g => (
            <li key={g.id}>
              <a href={`#${g.id}`}>{g.title}</a>
            </li>
          ))}
        </ul>
      </nav>

      {/* Getting Started */}
      <section id="getting-started" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">Getting Started</h2>
        <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Log in with your company credentials.</li>
          <li>Use the sidebar to navigate between Dashboard, Sensors, Alarms, and more.</li>
          <li>Click on a section to view details and perform actions.</li>
        </ol>
      </section>

      {/* View Sensor Data */}
      <section id="view-sensor-data" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">How to View Sensor Data</h2>
        <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Go to <b>Sensors</b> from the sidebar.</li>
          <li>Browse the list or use search to find a specific sensor.</li>
          <li>Click a sensor's name to view its detailed data and charts.</li>
          <li>Review live readings, historical charts, and alarm status.</li>
        </ol>
      </section>

      {/* Add Sensor */}
      <section id="add-sensor" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">How to Add a Sensor</h2>
        <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Go to <b>Sensors</b> from the sidebar.</li>
          <li>Click the <b>Add Sensor</b> button (usually at the top or above the sensor list).</li>
          <li>Fill in the required sensor details, such as name, serial number, type, and any other necessary information.</li>
          <li>Click <b>Save</b> or <b>Add</b> to register the new sensor.</li>
          <li>The new sensor will appear in the list and can be assigned to zones or configured further.</li>
        </ol>
      </section>

      {/* TS1 Portal / base unit Bluetooth setup */}
      <section id="configure-base-unit-ts1-portal" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">
          Configure a Base Unit with TS1 Portal (mobile)
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-3">
          The <strong>{TS1_PORTAL.displayName}</strong> app (Android and iOS) makes it much easier to set up a TS1 base
          unit before or after you add it on the <b>Base Units</b> page in this dashboard.
        </p>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mb-4">
          <li>
            <strong>Bluetooth</strong> — connect your phone to the base unit wirelessly for initial setup.
          </li>
          <li>
            <strong>Wi-Fi</strong> — choose the network and credentials the base unit should use.
          </li>
          <li>
            <strong>Ethernet</strong> — configure wired network details where applicable.
          </li>
          <li>
            <strong>Customer ID</strong> — set which customer account the base unit should publish telemetry to.
          </li>
        </ul>
        <p className="text-gray-700 dark:text-gray-300 mb-3">
          On the dashboard, open <b>Base Units</b> and click <b>Add Base Unit</b> — you will see a short reminder and
          store links for {TS1_PORTAL.displayName} when they are available.
        </p>
        <div className="flex flex-wrap gap-2">
          {TS1_PORTAL.androidStoreUrl ? (
            <a
              href={TS1_PORTAL.androidStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm underline text-blue-700 dark:text-blue-400"
            >
              {TS1_PORTAL.displayName} on Google Play
            </a>
          ) : null}
          {TS1_PORTAL.iosStoreUrl ? (
            <a
              href={TS1_PORTAL.iosStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm underline text-blue-700 dark:text-blue-400"
            >
              {TS1_PORTAL.displayName} on the App Store
            </a>
          ) : null}
        </div>
        {!TS1_PORTAL.androidStoreUrl && !TS1_PORTAL.iosStoreUrl ? (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Store links will be added to this page and the Base Units screen when the apps are listed — until then,
            search for <span className="font-medium">{TS1_PORTAL.displayName}</span> on Google Play or the App Store.
          </p>
        ) : null}
      </section>

      {/* EMS / ARB Network Setup */}
      <section id="ems-arb-network" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">EMS Base Units &amp; ARB Alarm Modules — Network Setup</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-3">
          EMS base units and ARB (Alarm Relay Base) modules communicate with the TS1 cloud over a TCP connection.
          They must be configured to send data to the following address:
        </p>
        <div className="my-4 px-4 py-3 rounded-lg bg-gray-100 dark:bg-gray-700 font-mono text-sm text-gray-900 dark:text-white">
          <div><span className="text-gray-500 dark:text-gray-400">Host:</span> switchback.proxy.rlwy.net</div>
          <div><span className="text-gray-500 dark:text-gray-400">Port:</span> 23017</div>
        </div>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
          <li>This applies to <strong>all EMS base units</strong> (Hybrid EMS) and <strong>all ARB alarm modules</strong> regardless of firmware version.</li>
          <li>TS1 base units do <strong>not</strong> use this address — they connect via MQTT and are configured separately using the TS1 Portal app.</li>
          <li>Make sure TCP port 23017 is <strong>not blocked</strong> by the site firewall or router. No inbound rules are needed — only outbound TCP on port 23017.</li>
          <li>If the unit is not appearing as online after configuration, verify network connectivity to this host and port from the installation site.</li>
        </ul>
      </section>

      {/* Manage Thresholds */}
      <section id="manage-thresholds" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">How to View or Edit Thresholds</h2>
        <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Go to the <b>Thresholds</b> page from the sidebar.</li>
          <li>Find the sensor or channel whose thresholds (alarm setpoints) you want to view or edit.</li>
          <li>Click the <b>Edit</b> or <b>Set Thresholds</b> button next to the relevant sensor/channel.</li>
          <li>Adjust the high and low alarm values as needed.</li>
          <li>Click <b>Save</b> to apply your changes. The new thresholds will take effect immediately.</li>
        </ol>
      </section>

      {/* Acknowledge Alarm */}
      <section id="acknowledge-alarm" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">How to Acknowledge an Alarm</h2>
        <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Go to <b>Alarm Log</b> from the sidebar (or use the dashboard sensor row bell when an alarm is active).</li>
          <li>Find the alarm event you want to acknowledge (unacknowledged alarms are highlighted).</li>
          <li>Click <b>Acknowledge</b> (or <b>Ack</b> on mobile). A dialog opens with alarm details.</li>
          <li>Enter a <b>required note</b> explaining the cause or action taken, then confirm.</li>
          <li>To acknowledge several events at once, select them and use <b>Ack. Selected</b> — you must enter a note that applies to all selected events.</li>
        </ol>
      </section>

      {/* Manage Users */}
      <section id="manage-users" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">How to Add or Remove a User</h2>
        <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Go to <b>Users</b> from the sidebar.</li>
          <li>To add a user, click <b>Add User</b> and fill in the required details.</li>
          <li>To remove a user, click the <b>Delete</b> or <b>Remove</b> button next to their name.</li>
          <li>Changes take effect immediately for all users.</li>
        </ol>
      </section>

      {/* Manage Zones */}
      <section id="manage-zones" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">How to View, Add, or Remove Zones</h2>
        <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Go to <b>Zones</b> from the sidebar.</li>
          <li>To view details, click on a zone's name in the list.</li>
          <li>To add a new zone, click <b>Add Zone</b> and fill in the required information.</li>
          <li>To remove a zone, click the <b>Delete</b> or <b>Remove</b> button next to the zone you wish to delete.</li>
          <li>Assign sensors or users to a zone using the available options in the zone details view.</li>
        </ol>
      </section>

      {/* Edit Alarm Notes */}
      <section id="edit-alarm-notes" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">How to Edit Notes on an Alarm</h2>
        <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Go to <b>Alarm Log</b> from the sidebar.</li>
          <li>Find the alarm event you want to add a note to.</li>
          <li>Click <b>Edit</b> next to the Notes field.</li>
          <li>Enter your note and click <b>Save Note</b>. Your username will be appended to the note.</li>
        </ol>
      </section>

      {/* Troubleshooting & FAQ */}
      <section id="troubleshooting" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">Troubleshooting & FAQ</h2>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
          <li><b>Can't log in?</b> Double-check your credentials and internet connection. If the problem persists, contact <a href="mailto:support@thermal-systems.co.uk" className="underline text-blue-700 dark:text-blue-400">support</a>.</li>
          <li><b>Data not updating?</b> Try refreshing the page. If the issue continues, check your network or contact support.</li>
          <li><b>Need more help?</b> Visit <a href="https://thermal-systems.co.uk/contact-us/" target="_blank" rel="noopener noreferrer" className="underline text-blue-700 dark:text-blue-400">Thermal Systems — Contact us</a> or email <a href="mailto:support@thermal-systems.co.uk" className="underline text-blue-700 dark:text-blue-400">support@thermal-systems.co.uk</a>.</li>
        </ul>
      </section>
    </div>
  );
} 